import api from './axiosConfig';

export interface FilePreviewResponse {
    data: Blob;
    filename?: string;
}

class FileService {
    /**
     * Clean file path by removing /uploads/ prefix if present
     * @param filePath - The file path to clean
     * @returns string - The cleaned file path
     */
    cleanFilePath(filePath: string): string {
        // Remove /uploads/ prefix if present
        if (filePath.startsWith('uploads/')) {
            return filePath.substring(8); // Remove 'uploads/' (8 characters)
        }
        if (filePath.startsWith('/uploads/')) {
            return filePath.substring(9); // Remove '/uploads/' (9 characters)
        }
        return filePath;
    }

    /**
     * Preview a file by its path
     * @param filePath - The file path to preview
     * @returns Promise<FilePreviewResponse> - The file data as blob
     */
    async previewFile(filePath: string): Promise<FilePreviewResponse> {
        try {
            const cleanedPath = this.cleanFilePath(filePath);
            const response = await api.get(`/preview/file/${cleanedPath}`, {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/json',
                },
            });

            // Extract filename from the original file path
            const filename = filePath.split('/').pop() || 'file';

            return {
                data: response.data,
                filename,
            };
        } catch (error) {
            console.error('Error previewing file:', error);
            throw error;
        }
    }

    /**
     * Download a file by its path
     * @param filePath - The file path to download
     * @param filename - Optional custom filename for download
     */
    async downloadFile(filePath: string, filename?: string): Promise<void> {
        try {
            const response = await this.previewFile(filePath);
            
            // Create a blob URL for download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            
            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || response.filename || 'download';
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
            throw error;
        }
    }

    private readonly knownExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'pdf'];

    /**
     * Get file extension from file path.
     * Handles backend filenames that append _uuid after the extension (e.g. "file.png_239f4a51-...").
     * @param filePath - The file path or filename
     * @returns string - The file extension
     */
    getFileExtension(filePath: string): string {
        const filename = filePath.split('/').pop() || '';
        const lastPart = filename.split('.').pop()?.toLowerCase() || '';
        if (this.knownExtensions.includes(lastPart)) return lastPart;
        // Backend may append _uuid after extension (e.g. "file.png_239f4a51-...")
        const extRegex = /\.(png|jpeg|jpg|gif|bmp|webp|svg|pdf)(?=[_.]|$)/i;
        const match = extRegex.exec(filename);
        return match ? match[1].toLowerCase() : lastPart;
    }

    /**
     * Check if file is an image
     * @param filePath - The file path
     * @returns boolean - True if file is an image
     */
    isImage(filePath: string): boolean {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        return imageExtensions.includes(this.getFileExtension(filePath));
    }

    /**
     * Check if file is a PDF
     * @param filePath - The file path
     * @returns boolean - True if file is a PDF
     */
    isPDF(filePath: string): boolean {
        return this.getFileExtension(filePath) === 'pdf';
    }

    /**
     * Check if file can be previewed in browser
     * @param filePath - The file path
     * @returns boolean - True if file can be previewed
     */
    canPreview(filePath: string): boolean {
        return this.isImage(filePath) || this.isPDF(filePath);
    }
}

export const fileService = new FileService(); 